import { cn } from '@/lib/utils';
import type { SubmissionAttemptPipelineStep } from '@/services/api/agentflowService';

const STEP_STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-500',
  blocked: 'bg-amber-500',
  running: 'bg-indigo-500 animate-pulse',
  pending: 'bg-zinc-300',
};

const STEP_LABEL_COLORS: Record<string, string> = {
  completed: 'text-green-700',
  blocked: 'text-amber-700',
  running: 'text-indigo-700',
  pending: 'text-zinc-500',
};

const STEP_CHIP_COLORS: Record<string, string> = {
  completed: 'border-green-200 bg-green-50/80',
  blocked: 'border-amber-200 bg-amber-50/80',
  running: 'border-indigo-200 bg-indigo-50/80',
  pending: 'border-zinc-200 bg-zinc-50',
};

interface PipelineStepIndicatorProps {
  steps: SubmissionAttemptPipelineStep[];
  summaryTitle?: string | null;
  summaryDescription?: string | null;
}

export const PipelineStepIndicator = ({
  steps,
  summaryTitle,
  summaryDescription,
}: PipelineStepIndicatorProps) => {
  if (steps.length === 0) return null;

  return (
    <div className="space-y-2">
      {summaryTitle && (
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-zinc-800">{summaryTitle}</p>
          {summaryDescription && (
            <p className="text-xs leading-relaxed text-zinc-500">
              {summaryDescription}
            </p>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2 gap-y-1.5">
        {steps.map((step) => (
          <div
            key={step.step_id}
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5',
              STEP_CHIP_COLORS[step.status] ?? 'border-zinc-200 bg-zinc-50',
            )}
          >
            <span
              className={cn(
                'inline-block h-2 w-2 rounded-full shrink-0',
                STEP_STATUS_COLORS[step.status] ?? 'bg-zinc-300',
              )}
            />
            <span
              className={cn(
                'text-xs font-medium',
                STEP_LABEL_COLORS[step.status] ?? 'text-zinc-500',
              )}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
